/**
 * Checks if number 'a' (formatted with comma as thousands separator and dot as decimal)
 * is strictly greater than number 'b' (same format).
 * Handles potential invalid number formats.
 *
 * @param a The first number string (e.g., "1,234.56").
 * @param b The second number string (e.g., "1,235.78").
 * @returns True if 'a' is strictly greater than 'b' numerically, false otherwise.
 */
export const numberIsGreaterThan = (
  a: string | null | undefined,
  b: string | null | undefined,
  allowEqual: boolean | undefined = true
): boolean => {
  if (!a && !b) return false; // If both are null or undefined, return false
  const numAStr = a?.replace(/,/g, "") ?? ""; // Remove commas before parsing
  const numBStr = b?.replace(/,/g, "") ?? ""; // Remove commas before parsing

  const numA = parseFloat(numAStr);
  const numB = parseFloat(numBStr);

  if (isNaN(numA) || isNaN(numB)) {
    return false; // Return false if either input is not a valid number
  }

  return allowEqual ? numA >= numB : numA > numB; // Compare the numbers
};

/**
 * Formats a string representation of a decimal number with commas as thousands separators.
 * Allows only digits and a single decimal point in the input before formatting.
 *
 * @param value The string value to format (e.g., "12345.67", "12345", ".5").
 * @returns The formatted string with commas for thousands (e.g., "12,345.67", "12,345", ".5").
 * Returns an empty string if the input value is undefined.
 *
 * @example
 * formatDecimalNumber("1234567.89"); // Returns "1,234,567.89"
 * formatDecimalNumber("1000");       // Returns "1,000"
 * formatDecimalNumber("123.45");     // Returns "123.45"
 * formatDecimalNumber(".5");        // Returns ".5"
 * formatDecimalNumber(undefined);    // Returns ""
 */
export const formatDecimalNumber = (value: string | undefined): string => {
  if (!value) return ""; // Return empty string if value is undefined
  const cleanedValue = value.replace(/[^\d.]/g, ""); // Allow digits and dot
  const parts = cleanedValue.split(".");
  const integerPart = parts[0]
    ? new Intl.NumberFormat().format(Number(parts[0]))
    : "";
  const decimalPart = parts[1] ? `.${parts[1]}` : "";
  return `${integerPart}${decimalPart}`;
};

/**
 * Sanitizes an input string to allow only digits, one decimal point, and commas (only before the decimal).
 *
 * @param rawValue The raw input string to sanitize.
 * @returns The sanitized string containing only allowed characters.
 */
export const sanitizeDecimalInput = (rawValue: string): string => {
  let sanitizedValue = "";
  let hasDecimal = false;

  for (let i = 0; i < rawValue.length; i++) {
    const char = rawValue[i];
    if (/\d/.test(char)) {
      sanitizedValue += char;
    } else if (char === "." && !hasDecimal) {
      sanitizedValue += char;
      hasDecimal = true;
    } else if (char === "," && !hasDecimal) {
      sanitizedValue += char;
    }
  }

  return sanitizedValue;
};

/**
 * Returns the next number with the smallest decimal step
 * based on the visible decimal length of the input.
 * E.g. "0.900" -> 0.91, "0.94565" -> 0.94566, "1" -> 2
 */
export function getNextNumber(strOrNum: string | number): number {
  const decimals = getVisibleDecimalCount(strOrNum);
  let step = 1;
  if (decimals > 0) {
    step = Math.pow(10, -decimals);
  }
  const n = Number(strOrNum);
  return Math.round((n + step) * (1 / step)) / (1 / step);
}

/**
 * Returns the previous number with the smallest decimal step
 * based on the visible decimal length of the input.
 * E.g. "0.900" -> 0.89, "0.94565" -> 0.94564, "1" -> 0
 */
export function getPreviousNumber(strOrNum: string | number): number {
  const decimals = getVisibleDecimalCount(strOrNum);
  let step = 1;
  if (decimals > 0) {
    step = Math.pow(10, -decimals);
  }
  const n = Number(strOrNum);
  return Math.round((n - step) * (1 / step)) / (1 / step);
}

/**
 *  Counts the number of visible decimal places in a string or number.
 *  For example, "0.900" has 3 visible decimal places, while "0.94565" has 5.
 *  If the input is an integer or has no decimal part, it returns 0.
 * @param value - A string or number to check for visible decimal places.
 * @returns
 */
export const getVisibleDecimalCount = (value: string | number): number => {
  const str = String(value);
  const decimalPart = str.split(".")[1];
  return decimalPart ? decimalPart.length : 0;
};

/**
 * Returns the smallest number greater than the input,
 * ensuring it formats differently for display purposes.
 *
 * Useful for range labeling (e.g., quantiles) where values like "30 - 30" must be avoided.
 * The increment is based on the number of visible decimal digits (for "decimal" format)
 * or at least 1 for "integer" format.
 *
 * Behavior:
 * - For `formatType: "decimal"`: Adds the smallest possible increment based on visible decimals.
 * - For `formatType: "integer"`: Increments to the next formatted integer greater than input.
 *
 * Examples:
 * ```ts
 * getNextNumberSafeFormatted("0.900", "decimal");    // → 0.901
 * getNextNumberSafeFormatted("0.94565", "decimal");  // → 0.94566
 * getNextNumberSafeFormatted("1", "integer");        // → 2
 * getNextNumberSafeFormatted(29.8, "integer");       // → 31
 * ```
 *
 * @param strOrNum - The input number as a string or number.
 * @param formatType - Either "integer" or "decimal" (default: "decimal").
 * @returns A number slightly greater than the input, with distinct formatted output.
 */

export const getNextNumberSafeFormatted = (
  strOrNum: string | number,
  formatType: "integer" | "decimal" = "decimal"
): number => {
  const base = Number(strOrNum);

  if (Number.isNaN(base)) {
    return 0;
  }

  const rawString = String(strOrNum);
  const decimals =
    formatType === "integer"
      ? 0
      : Math.max(1, getVisibleDecimalCount(rawString));

  const step = Math.pow(10, -decimals);

  let next = base + step;

  if (Number(next.toFixed(decimals)) <= base) {
    next += step;
  }
  return Number(next.toFixed(decimals));
};

/**
 * Rounds a number **upward** (using `Math.ceil`) to a fixed number of decimal places,
 * **only if** the original number has more than the specified number of decimal digits.
 *
 * If the number already has equal to or fewer decimal places than `decimals`,
 * the original value is returned unchanged.
 *
 * ⚠️ Note:
 * This function relies on `value.toString()` for checking decimal length,
 * which may be affected by JavaScript’s floating-point precision limitations.
 *
 * @example
 * roundUpDecimal(1108.7, 3)                   // 1108.7   (no rounding needed)
 * roundUpDecimal(1108.71, 3)                  // 1108.71  (no rounding needed)
 * roundUpDecimal(1108.718, 3)                 // 1108.718 (no rounding needed)
 * roundUpDecimal(1108.7184, 3)                // 1108.719 (rounded up)
 * roundUpDecimal(1108.71899, 3)               // 1108.719
 * roundUpDecimal(1108.7180000001, 3)          // 1108.719
 * roundUpDecimal(1108.718000000059, 3)        // 1108.719
 *
 * @param value - The numeric value to evaluate and potentially round up
 * @param decimals - The maximum allowed number of decimal places
 * @returns The original number if decimal places are within the limit, or the value rounded up to the specified precision
 */
export const roundUpDecimal = (value: number, decimals: number): number => {
  const parts = value.toString().split(".");
  const fraction = parts[1] || "";

  if (fraction.length <= decimals) {
    return value;
  }

  const factor = Math.pow(10, decimals);
  return Math.ceil(value * factor) / factor;
};

/**
 * Formats a number to a maximum of N decimal places.
 * If the number has more than N decimal places, it's truncated to N.
 * Otherwise, it's returned as is.
 *
 * @param num The number to format.
 * @param maxDecimal The maximum number of decimal places.
 * @returns The formatted number or the original number if it has N or fewer decimal places.
 */
export const formatNumberWithMaxDecimal = (
  num: number,
  maxDecimal: number
): string => {
  const numStr = String(num);
  if (numStr.includes(".")) {
    const decimalPart = numStr.split(".")[1];
    if (decimalPart.length > maxDecimal) {
      return num.toFixed(maxDecimal);
    }
  }
  return String(num);
};

/**
 * Determines whether decimal precision should be preserved based on the range values.
 * Decimals are preserved when:
 * 1. Either min or max has decimals AND the range difference is less than 1
 * 2. OR if either value is less than 1
 *
 * @param min - Minimum value of the range
 * @param max - Maximum value of the range
 * @returns boolean indicating whether to preserve decimals
 */
export const shouldPreserveDecimals = (
  min: number | null | undefined,
  max: number | null | undefined
): boolean => {
  if (!min && !max) return false;

  const hasDecimals = (min && min % 1 !== 0) || (max && max % 1 !== 0);

  const isSmallRange = min != null && max != null && Math.abs(max - min) < 1;

  const hasSmallValues = (min != null && min < 1) || (max != null && max < 1);

  return (hasDecimals && isSmallRange) || hasSmallValues;
};

/**
 * Processes numeric range values with appropriate decimal handling
 * @param range - Object containing min and max values
 * @returns Processed min and max values
 */
export const processNumericRange = (range: {
  min: number | null | undefined;
  max: number | null | undefined;
}) => {
  const preserveDecimals = shouldPreserveDecimals(range.min, range.max);
  return {
    min: preserveDecimals ? (range.min ?? 0) : Math.floor(range.min ?? 0),
    max: preserveDecimals ? (range.max ?? 0) : roundUpDecimal(range.max ?? 0, 0)
  };
};
